import { useNavigate } from 'react-router-dom';
import { Button, BookCover } from '@plush-book/ui';
import type { CoverBackground, SceneType, AnimalType } from '@plush-book/ui';
import { StepHeader } from '../components/StepHeader';
import { useGeneratorStore } from '../state/generatorStore';
import { STORY_TEMPLATES_BY_ID } from '@plush-book/story-templates';
import { formatEuro, PRICING } from '@plush-book/shared';

const ANIMAL_BY_STORY: Record<string, AnimalType> = {
  sleep_star: 'bear',
  little_big_adventure: 'rabbit',
  birthday_surprise: 'dino',
};

export function Preview() {
  const nav = useNavigate();
  const { storyTemplateId, plushies, upsellSecondCopy, setSecondCopy } = useGeneratorStore();
  const story = storyTemplateId ? STORY_TEMPLATES_BY_ID[storyTemplateId] : undefined;
  const mainPlush = plushies[0]?.name || 'dein Kuscheltier';

  const totalCents =
    PRICING.basePriceCents +
    Math.max(plushies.length - 1, 0) * PRICING.additionalPlushCents +
    (upsellSecondCopy ? PRICING.secondCopyCents : 0);

  function handleOrder() {
    // Phase 3 wires this to /cart/add.js on the Shopify product page
    const bookId = useGeneratorStore.getState().bookId;
    const target = `${import.meta.env.VITE_SHOPIFY_PRODUCT_URL ?? '#'}?book_id=${bookId ?? ''}`;
    window.location.href = target;
  }

  return (
    <div className="shell">
      <StepHeader active={3} />
      <div className="scroll" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              padding: '4px 10px',
              background: 'var(--sage)',
              color: '#fff',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              marginBottom: 8,
            }}
          >
            VORSCHAU FERTIG
          </div>
          <h2
            style={{
              fontFamily: 'var(--serif)',
              fontSize: 28,
              fontWeight: 500,
              margin: 0,
              letterSpacing: -0.3,
            }}
          >
            Dein erstes Buchbild ist fertig
          </h2>
        </div>

        <div style={{ marginTop: 22, width: 240, margin: '22px auto 0' }}>
          <BookCover
            background={(story?.cover.background ?? 'sky') as CoverBackground}
            scene={(story?.cover.scene ?? 'night') as SceneType}
            animal={ANIMAL_BY_STORY[storyTemplateId ?? ''] ?? 'bear'}
            titleLine1={`${mainPlush} &`}
            titleLine2={story?.title.split(' ').slice(-2).join(' ') ?? ''}
            showSpine
          />
        </div>

        <div
          style={{
            marginTop: 24,
            padding: 14,
            background: 'var(--bg-card)',
            borderRadius: 14,
            border: '1px solid var(--line)',
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-mute)',
              fontFamily: 'var(--mono)',
              letterSpacing: 1.2,
              marginBottom: 8,
            }}
          >
            BEISPIELSEITE · S.3
          </div>
          <div
            style={{
              aspectRatio: '16/10',
              borderRadius: 8,
              background: 'linear-gradient(160deg, #2C3E54, #4D6781)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '40%',
                transform: 'translateX(-50%)',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(160deg, #C8916A, #8E5B3E)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: 18,
                top: 16,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#F8EBB7',
              }}
            />
          </div>
          <div
            style={{
              marginTop: 12,
              fontFamily: 'var(--serif)',
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--ink)',
              fontStyle: 'italic',
            }}
          >
            „Als es im Kinderzimmer leise wurde, öffnete{' '}
            <strong style={{ color: 'var(--terracotta-deep)' }}>{mainPlush}</strong> vorsichtig die
            Augen."
          </div>
        </div>

        <div
          style={{
            marginTop: 18,
            padding: 16,
            background: 'var(--bg-card)',
            borderRadius: 14,
            border: '1px solid var(--line)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
            <Row k="Geschichte" v={story?.title ?? '–'} />
            <Row k="Kuscheltiere" v={plushies.map((p) => p.name).join(', ')} />
            <Row k="Anzahl" v={`${plushies.length} Kuscheltier${plushies.length > 1 ? 'e' : ''}`} />
            <Row k="Format" v="16 Seiten · DIN A5" />
            <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-mute)' }}>Preis</span>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 600 }}>
                {formatEuro(totalCents)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                color: 'var(--ink-mute)',
              }}
            >
              <span>Versand</span>
              <span>im Checkout</span>
            </div>
          </div>
        </div>

        <label
          style={{
            marginTop: 16,
            padding: 14,
            background: 'var(--bg-card)',
            border: upsellSecondCopy
              ? '1.5px solid var(--terracotta)'
              : '1.5px solid var(--terracotta)',
            borderRadius: 14,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -10,
              left: 14,
              padding: '2px 8px',
              background: 'var(--terracotta)',
              color: '#fff',
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            BELIEBT
          </div>
          <div style={{ fontSize: 22 }}>🎁</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>
              Zweites Exemplar für Oma & Opa?
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-soft)', marginTop: 2 }}>
              Identisches Buch — als Geschenk verpackt
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--terracotta-deep)' }}>
              +{formatEuro(PRICING.secondCopyCents)}
            </span>
            <input
              type="checkbox"
              checked={upsellSecondCopy}
              onChange={(e) => setSecondCopy(e.target.checked)}
              style={{ transform: 'scale(1.3)' }}
            />
          </div>
        </label>

        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: 'var(--sand)',
            borderRadius: 10,
            fontSize: 11,
            lineHeight: 1.5,
            color: 'var(--ink-soft)',
          }}
        >
          ✦ Nach dem Kauf erstellen wir dein vollständiges Buch. Du erhältst vor dem Druck eine
          digitale Vorschau und kannst eine kostenlose Korrektur anfragen.
        </div>
      </div>
      <div className="sticky-cta" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <Button variant="primary" size="md" fullWidth onClick={handleOrder}>
          Ganzes Buch bestellen — {formatEuro(totalCents)}
        </Button>
        <button
          onClick={() => nav('/personal')}
          style={{
            width: '100%',
            background: 'transparent',
            color: 'var(--ink-soft)',
            border: 0,
            padding: '12px',
            fontSize: 13,
            fontWeight: 600,
            marginTop: 4,
            cursor: 'pointer',
          }}
        >
          Angaben ändern
        </button>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--ink-mute)' }}>{k}</span>
      <span style={{ color: 'var(--ink)', fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
}
